/**
 * Phase B2.6 — SA admission stages API client + types.
 *
 * Wraps the BE surface added in eduviaos-backend PR #53:
 *
 *   GET    /super-admin/tenants/{slug}/admission-stages
 *   PUT    /super-admin/tenants/{slug}/admission-stages
 *   POST   /super-admin/tenants/{slug}/admission-stages/reset
 *   PATCH  /super-admin/tenants/{slug}/admission-stages/{stage_key}/form
 *   GET    /super-admin/tenants/{slug}/admission-stages/funnel
 *
 * The wire shapes mirror `app/domains/onboarding/schemas.py` exactly —
 * any field shape change in the BE must land here too.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type TerminalOutcome = "admitted" | "rejected" | null;

export interface LinkedFormSummary {
  slug: string;
  name: string;
  version: number;
  field_count: number;
}

/** One action-gated requirement on a stage (Phase F0/F3).
 *
 *  Mirrors `app.domains.onboarding.schemas.StageRequirementItem` 1:1.
 *  Evaluators ship per F-slice — the SA can configure all five kinds
 *  today, but only `form_submission` and `manual_check` evaluate
 *  end-to-end in F0. The other three are reserved for F4
 *  (document_upload), F8 (assessment_attempt), and F13
 *  (interview_completed) and surface as "Pending — evaluator not yet
 *  shipped" on the IA detail page until those slices land. */
export type StageRequirementKind =
  | "form_submission"
  | "document_upload"
  | "assessment_attempt"
  | "interview_completed"
  | "manual_check";

export interface StageRequirementWire {
  key: string;
  kind: StageRequirementKind;
  label: string;
  config: Record<string, unknown>;
}

export interface AdmissionStageItem {
  key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: TerminalOutcome;
  color_hint: string | null;
  auto_advance_days: number | null;
  required_documents: string[];
  linked_form: LinkedFormSummary | null;
  requirements: StageRequirementWire[];
}

export type AdmissionStageSource = "tenant" | "vertical_default";

export interface AdmissionStagesResponse {
  tenant_slug: string;
  vertical: string;
  source: AdmissionStageSource;
  allow_direct_admission: boolean;
  allow_skip_stages: boolean;
  allow_revert: boolean;
  duplicate_detection: boolean;
  stages: AdmissionStageItem[];

  /** CAS token for the tenant's admission-draft override doc (Phase
   *  F-finale-4 Slice 6-BE). Int seconds-since-epoch derived from
   *  `AdmissionStageConfig.updated_at`; `0` when the tenant has no override
   *  yet (`source === "vertical_default"`). The capability-AI assist flow
   *  echoes this on suggest + apply so the BE can 409 a stale suggestion. */
  draft_version: number;
}

/** Wire shape sent on PUT — mirrors `WriteAdmissionStagesRequest`. The
 *  BE accepts a list of dicts so it can run the typed payload schema
 *  validators (≤10 stages, contiguous orders, etc.) before persistence. */
export interface WriteAdmissionStageBody {
  key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: TerminalOutcome;
  color_hint?: string | null;
  auto_advance_days?: number | null;
  required_documents?: string[];
  /** Action-gated requirements (Phase F0/F3). Capability validator
   *  enforces key uniqueness within the stage, kind enum membership,
   *  and DNS-safe key slug. Empty list means "legacy admin-clicks-
   *  to-advance" path. */
  requirements?: StageRequirementWire[];
}

export interface WriteAdmissionStagesRequest {
  stages: WriteAdmissionStageBody[];
  allow_direct_admission: boolean;
  allow_skip_stages: boolean;
  allow_revert: boolean;
  duplicate_detection: boolean;
}

export interface FunnelStageItem {
  stage_key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: TerminalOutcome;
  color_hint: string | null;
  count: number;
}

export interface AdmissionFunnelResponse {
  tenant_slug: string;
  vertical: string;
  stages: FunnelStageItem[];
  total: number;
}

export interface LinkStageFormRequest {
  /** `null` to unlink the stage from any form. */
  form_slug: string | null;
}

// ── React Query hooks ─────────────────────────────────────────────────

const stagesKey = (slug: string) => ["super-admin", "admission-stages", slug] as const;
const funnelKey = (slug: string) => ["super-admin", "admission-stages", slug, "funnel"] as const;

export function useAdmissionStages(slug: string) {
  return useQuery<AdmissionStagesResponse>({
    queryKey: stagesKey(slug),
    queryFn: () =>
      api.get<AdmissionStagesResponse>(
        `/super-admin/tenants/${encodeURIComponent(slug)}/admission-stages`,
      ),
    enabled: Boolean(slug),
  });
}

export function useAdmissionFunnel(slug: string) {
  return useQuery<AdmissionFunnelResponse>({
    queryKey: funnelKey(slug),
    queryFn: () =>
      api.get<AdmissionFunnelResponse>(
        `/super-admin/tenants/${encodeURIComponent(slug)}/admission-stages/funnel`,
      ),
    enabled: Boolean(slug),
  });
}

export function useWriteAdmissionStages(slug: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionStagesResponse, ApiError, WriteAdmissionStagesRequest>({
    mutationFn: (body) =>
      api.put<AdmissionStagesResponse>(
        `/super-admin/tenants/${encodeURIComponent(slug)}/admission-stages`,
        body,
      ),
    onSuccess: (data) => {
      qc.setQueryData(stagesKey(slug), data);
      qc.invalidateQueries({ queryKey: funnelKey(slug) });
    },
  });
}

export function useResetAdmissionStages(slug: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionStagesResponse, ApiError, void>({
    mutationFn: () =>
      api.post<AdmissionStagesResponse>(
        `/super-admin/tenants/${encodeURIComponent(slug)}/admission-stages/reset`,
        undefined,
      ),
    onSuccess: (data) => {
      qc.setQueryData(stagesKey(slug), data);
      qc.invalidateQueries({ queryKey: funnelKey(slug) });
    },
  });
}

export function useLinkStageForm(slug: string) {
  const qc = useQueryClient();
  return useMutation<
    AdmissionStagesResponse,
    ApiError,
    { stageKey: string; body: LinkStageFormRequest }
  >({
    mutationFn: ({ stageKey, body }) =>
      api.patch<AdmissionStagesResponse>(
        `/super-admin/tenants/${encodeURIComponent(slug)}/admission-stages/${encodeURIComponent(
          stageKey,
        )}/form`,
        body,
      ),
    onSuccess: (data) => {
      qc.setQueryData(stagesKey(slug), data);
      qc.invalidateQueries({ queryKey: funnelKey(slug) });
    },
  });
}

// ── Helpers ───────────────────────────────────────────────────────────

/** Default colour palette indexed by stage order — mirrors the chart
 *  scale already used on DashboardPage so funnel + dashboard read with
 *  the same vocabulary. Stages with `color_hint` set override this. */
export const STAGE_FALLBACK_COLORS = [
  "var(--color-chart-1, oklch(0.62 0.15 155))",
  "var(--color-chart-2, oklch(0.6 0.16 270))",
  "var(--color-chart-3, oklch(0.7 0.17 50))",
  "var(--color-chart-4, oklch(0.65 0.13 320))",
  "var(--color-chart-5, oklch(0.6 0.05 250))",
  "var(--color-chart-1, oklch(0.62 0.15 155))",
  "var(--color-chart-2, oklch(0.6 0.16 270))",
  "var(--color-chart-3, oklch(0.7 0.17 50))",
  "var(--color-chart-4, oklch(0.65 0.13 320))",
  "var(--color-chart-5, oklch(0.6 0.05 250))",
] as const;

export function resolveStageColor(stage: { color_hint: string | null; order: number }): string {
  if (stage.color_hint && stage.color_hint.trim() !== "") return stage.color_hint;
  const idx =
    ((stage.order % STAGE_FALLBACK_COLORS.length) + STAGE_FALLBACK_COLORS.length) %
    STAGE_FALLBACK_COLORS.length;
  return STAGE_FALLBACK_COLORS[idx] ?? STAGE_FALLBACK_COLORS[0]!;
}

/** DNS-safe slug derivation used when an SA renames a stage and the BE
 *  needs a stage-key for the rename. We never mutate an EXISTING key
 *  client-side (the BE has an immutability guard if there are live
 *  enquiries) — this helper is for net-new stages only, but we also
 *  use it to suggest a starter slug in the inline-rename input. */
export function deriveStageKey(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "stage"
  );
}

/** DNS-safe slug derivation for a requirement key (Phase F3). Same
 *  shape as `deriveStageKey` but capped at 60 chars to match the
 *  capability schema's `Field(min_length=2, max_length=60)`. */
export function deriveRequirementKey(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60) || "requirement"
  );
}

/** Human-readable label for each requirement kind. Indexed so the
 *  Select renders the same vocabulary as the BE error envelope when
 *  the capability validator rejects an invalid kind. */
export const REQUIREMENT_KIND_LABELS: Record<StageRequirementKind, string> = {
  form_submission: "Form submission",
  document_upload: "Document upload",
  assessment_attempt: "Assessment attempt",
  interview_completed: "Interview completed",
  manual_check: "Manual check",
};

/** Per-kind helper text rendered under the kind selector. Reflects the
 *  evaluator status documented in
 *  `app/domains/capability/payload_schemas/admission_stage.py`. */
export const REQUIREMENT_KIND_HELP: Record<StageRequirementKind, string> = {
  form_submission:
    "Linked form on this stage must have a saved payload on the enquiry. Evaluator ships in Phase F0.",
  manual_check:
    "Admin-side toggle on the IA detail page (e.g. 'ID document verified'). Evaluator ships in Phase F0.",
  document_upload:
    "A named document slot must have a non-null upload reference on the enquiry. Evaluator ships in Phase F4.",
  assessment_attempt:
    "Linked assessment template must have a graded attempt. Evaluator ships in Phase F8.",
  interview_completed:
    "Linked interview must be conducted with a non-empty outcome. Evaluator ships in Phase F13.",
};

/** True when the BE will run a real evaluator for this kind in the
 *  currently-shipped F-slice. The FE renders a "preview" badge for
 *  kinds where the evaluator is reserved for a later slice — the SA
 *  can still save the requirement, the BE will accept it, but the
 *  IA detail page will surface "Pending: evaluator not yet shipped". */
export const REQUIREMENT_KIND_LIVE_EVALUATOR: Record<StageRequirementKind, boolean> = {
  form_submission: true,
  manual_check: true,
  document_upload: false,
  assessment_attempt: false,
  interview_completed: false,
};
